import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Users, DollarSign, TrendingUp, BookOpen, Headphones, MapPin } from 'lucide-react';

import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { isUnauthorizedError, redirectToLogin } from '@/lib/authUtils';
import { PublicShell } from '@/components/public-shell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

interface CreatorApplicationStatus {
  id: string;
  status: string;
  adminNotes?: string | null;
  createdAt: string;
}

export default function BecomePartner() {
  const { toast } = useToast();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    displayName: '',
    phoneNumber: '',
    bio: '',
    creatorHeadline: '',
    experience: '',
    portfolioUrl: '',
    proposedContent: '',
    experienceLevel: '',
    hasExistingAudience: false,
    audienceDescription: '',
    primaryLocation: '',
    referralSource: '',
  });

  const { data: creatorApplication, refetch: refetchApplication } =
    useQuery<CreatorApplicationStatus | null>({
      queryKey: ['/api/creator/applications/current'],
      enabled: isAuthenticated && user?.role === 'user',
    });

  useEffect(() => {
    if (!user || formData.displayName) return;

    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    if (fullName) {
      setFormData(prev => ({ ...prev, displayName: fullName }));
    }
  }, [formData.displayName, user]);

  const applicationMutation = useMutation({
    mutationFn: async (applicationData: {
      displayName: string;
      phoneNumber?: string;
      bio?: string;
      creatorHeadline?: string;
      experience?: string;
      portfolioUrl?: string;
      proposedContent: string;
      experienceLevel: string;
      hasExistingAudience: boolean;
      audienceDescription?: string;
      primaryLocation?: string;
      referralSource?: string;
    }) => {
      await apiRequest('POST', '/api/creator/applications', applicationData);
    },
    onSuccess: () => {
      toast({
        title: 'Application Submitted!',
        description:
          "Thank you for your interest. We'll review your application and follow up by email.",
      });
      void queryClient.invalidateQueries({ queryKey: ['/api/creator/applications/current'] });
      void refetchApplication();
      setFormData({
        displayName: '',
        phoneNumber: '',
        bio: '',
        creatorHeadline: '',
        experience: '',
        portfolioUrl: '',
        proposedContent: '',
        experienceLevel: '',
        hasExistingAudience: false,
        audienceDescription: '',
        primaryLocation: '',
        referralSource: '',
      });
    },
    onError: (error: Error) => {
      if (isUnauthorizedError(error)) {
        redirectToLogin(500);
        return;
      }

      toast({
        title: 'Error',
        description: error.message || 'Failed to submit application. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const formatWebsiteUrl = (value: string) => {
    if (!value) return '';

    // If the value doesn't start with http:// or https://, add https://
    if (value && !value.match(/^https?:\/\//)) {
      return `https://${value}`;
    }
    return value;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePortfolioBlur = () => {
    if (formData.portfolioUrl) {
      const formattedUrl = formatWebsiteUrl(formData.portfolioUrl);
      setFormData(prev => ({ ...prev, portfolioUrl: formattedUrl }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!isAuthenticated) {
      redirectToLogin();
      return;
    }

    if (user?.role !== 'user') {
      toast({
        title: 'Creator access already active',
        description: 'This application is only needed for standard user accounts.',
      });
      return;
    }

    if (!formData.displayName.trim()) {
      toast({
        title: 'Missing Display Name',
        description: 'Please enter the public name you want to teach under.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.phoneNumber.trim()) {
      toast({
        title: 'Missing Phone Number',
        description: 'Please enter the best phone number for application follow-up.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.proposedContent.trim()) {
      toast({
        title: 'Missing Class Idea',
        description: "Please describe what you'd like to teach.",
        variant: 'destructive',
      });
      return;
    }

    if (!formData.experienceLevel.trim()) {
      toast({
        title: 'Missing Experience Level',
        description: 'Please select your experience level.',
        variant: 'destructive',
      });
      return;
    }

    applicationMutation.mutate(formData);
  };

  return (
    <PublicShell routeKey="creator-apply">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-accent text-white py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Teach with Kalovate</h1>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Share what you know with people in your community. Kalovate gives local creators a
            simple way to offer classes, manage sessions, and get paid — without building their own
            platform.
          </p>
          <div className="flex justify-center">
            <Badge className="bg-white/20 text-white text-sm px-4 py-2">
              Applications reviewed on a rolling basis
            </Badge>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">What You Get as a Creator</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Kalovate handles the logistics so you can focus on teaching.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="text-center p-6">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Local Discovery</h3>
                <p className="text-muted-foreground">
                  Your classes surface automatically on our local calendar and browse pages — in
                  front of people already looking to learn in your area.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  Your Own Creator Page
                </h3>
                <p className="text-muted-foreground">
                  A dedicated profile with your bio, upcoming sessions, and past classes — a single
                  link you can share anywhere.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  Keep 90% of What You Earn
                </h3>
                <p className="text-muted-foreground">
                  Transparent payouts — 90% to you, 10% to Kalovate — released 48 hours after each
                  session ends. No hidden fees.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  Roster and Enrollment
                </h3>
                <p className="text-muted-foreground">
                  Kalovate handles signup, seat capacity, and payment collection. You get a clean
                  roster before every session.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Headphones className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Real Support</h3>
                <p className="text-muted-foreground">
                  Reach us directly at admin@kalovate.com. We review every creator personally and
                  stay reachable throughout.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-6">
              <CardContent className="pt-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  Teach Anywhere You Work
                </h3>
                <p className="text-muted-foreground">
                  List your own space as the venue, or teach online. You set the location — we show
                  it on the map and calendar.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-muted">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">Ready to Get Started?</h2>
            <p className="text-lg text-muted-foreground">
              Tell us about yourself and your expertise. We'll review your application and get back
              to you soon.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Creator Application</CardTitle>
            </CardHeader>
            <CardContent>
              {authLoading ? (
                <div className="flex min-h-48 items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              ) : !isAuthenticated ? (
                <div className="space-y-4 text-center">
                  <p className="text-muted-foreground">
                    Sign in first so your creator application can be tied to your Kalovate account.
                  </p>
                  <Button
                    type="button"
                    className="rounded-full px-6"
                    onClick={() => redirectToLogin()}
                  >
                    Sign in to apply
                  </Button>
                </div>
              ) : user?.role === 'creator' ? (
                <div className="space-y-4 text-center">
                  <Badge className="rounded-full px-3 py-1">Creator access active</Badge>
                  <p className="text-muted-foreground">
                    Your account already has creator access. You can manage classes from the creator
                    dashboard.
                  </p>
                  <a href="/creator-dashboard">
                    <Button className="rounded-full px-6">Go to creator dashboard</Button>
                  </a>
                </div>
              ) : user?.role === 'admin' ? (
                <div className="space-y-4 text-center">
                  <Badge className="rounded-full px-3 py-1">Admin account</Badge>
                  <p className="text-muted-foreground">
                    Admin accounts already have access to creator and class management tools.
                  </p>
                  <a href="/admin">
                    <Button className="rounded-full px-6">Go to admin dashboard</Button>
                  </a>
                </div>
              ) : creatorApplication?.status === 'pending' ? (
                <div className="space-y-4 text-center">
                  <Badge variant="secondary" className="rounded-full px-3 py-1">
                    Application pending
                  </Badge>
                  <p className="text-muted-foreground">
                    Your creator application is under review. We’ll follow up by email when a
                    decision is made.
                  </p>
                </div>
              ) : creatorApplication?.status === 'approved' ? (
                <div className="space-y-4 text-center">
                  <Badge className="rounded-full px-3 py-1">Application approved</Badge>
                  <p className="text-muted-foreground">
                    Your creator application has been approved. Sign out and back in if creator
                    dashboard access has not appeared yet.
                  </p>
                  <a href="/creator-dashboard">
                    <Button className="rounded-full px-6">Go to creator dashboard</Button>
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {creatorApplication?.status === 'rejected' && (
                    <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
                      <p className="text-sm font-medium text-destructive">
                        Your previous application was not approved.
                      </p>
                      {creatorApplication.adminNotes && (
                        <p className="mt-2 text-sm text-muted-foreground">
                          Notes: {creatorApplication.adminNotes}
                        </p>
                      )}
                      <p className="mt-2 text-sm text-muted-foreground">
                        You can submit a new application below.
                      </p>
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Public creator name *
                      </label>
                      <Input
                        value={formData.displayName}
                        onChange={e => handleInputChange('displayName', e.target.value)}
                        placeholder="Your creator or business name"
                        data-testid="input-display-name"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Specialty or headline
                      </label>
                      <Input
                        value={formData.creatorHeadline}
                        onChange={e => handleInputChange('creatorHeadline', e.target.value)}
                        placeholder="e.g., Coffee education, ceramics, yoga"
                        data-testid="input-creator-headline"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Phone number *
                    </label>
                    <Input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={e => handleInputChange('phoneNumber', e.target.value)}
                      placeholder="Best number for application follow-up"
                      data-testid="input-phone-number"
                      required
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Short bio
                    </label>
                    <Textarea
                      value={formData.bio}
                      onChange={e => handleInputChange('bio', e.target.value)}
                      placeholder="A short public bio for your creator profile"
                      rows={3}
                      data-testid="input-bio"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Experience
                    </label>
                    <Textarea
                      value={formData.experience}
                      onChange={e => handleInputChange('experience', e.target.value)}
                      placeholder="Tell us about your background, qualifications, and teaching experience..."
                      rows={3}
                      data-testid="textarea-experience"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      What would you teach? *
                    </label>
                    <Textarea
                      value={formData.proposedContent}
                      onChange={e => handleInputChange('proposedContent', e.target.value)}
                      placeholder="Describe the classes or workshops you'd like to offer..."
                      rows={4}
                      data-testid="textarea-proposed-content"
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Experience level *
                      </label>
                      <select
                        value={formData.experienceLevel}
                        onChange={e => handleInputChange('experienceLevel', e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="select-experience-level"
                        required
                      >
                        <option value="">Select…</option>
                        <option value="hobbyist">Hobbyist</option>
                        <option value="semi_professional">Semi-professional</option>
                        <option value="professional">Professional</option>
                        <option value="expert">Expert</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        Primary city or region
                      </label>
                      <Input
                        value={formData.primaryLocation}
                        onChange={e => handleInputChange('primaryLocation', e.target.value)}
                        placeholder="e.g., Bloomington, IN or Online"
                        data-testid="input-primary-location"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Portfolio/Website
                    </label>
                    <Input
                      type="url"
                      value={formData.portfolioUrl}
                      onChange={e => handleInputChange('portfolioUrl', e.target.value)}
                      onBlur={handlePortfolioBlur}
                      placeholder="https://your-portfolio.com"
                      data-testid="input-portfolio"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Share a link to your work, portfolio, or professional website.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground block">
                      Are you currently teaching classes?
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setFormData(prev => ({
                            ...prev,
                            hasExistingAudience: true,
                          }))
                        }
                        className={`rounded-full border px-4 py-2 text-sm ${formData.hasExistingAudience ? 'bg-foreground text-background' : 'border-border bg-card text-foreground'}`}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData(prev => ({
                            ...prev,
                            hasExistingAudience: false,
                            audienceDescription: '',
                          }))
                        }
                        className={`rounded-full border px-4 py-2 text-sm ${!formData.hasExistingAudience ? 'bg-foreground text-background' : 'border-border bg-card text-foreground'}`}
                      >
                        No
                      </button>
                    </div>
                    {formData.hasExistingAudience && (
                      <Textarea
                        value={formData.audienceDescription}
                        onChange={e => handleInputChange('audienceDescription', e.target.value)}
                        placeholder="Tell us what you teach now, where you teach, and how often..."
                        rows={2}
                        data-testid="textarea-audience-description"
                      />
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      How did you hear about Kalovate?
                    </label>
                    <select
                      value={formData.referralSource}
                      onChange={e => handleInputChange('referralSource', e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="select-referral-source"
                    >
                      <option value="">Prefer not to say</option>
                      <option value="friend_referral">A friend or colleague</option>
                      <option value="social_media">Social media</option>
                      <option value="search_engine">Search engine</option>
                      <option value="local_event">Local event or community</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <Button
                    type="submit"
                    disabled={applicationMutation.isPending}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-lg py-6"
                    data-testid="button-submit-application"
                  >
                    {applicationMutation.isPending ? 'Submitting...' : 'Submit Application'}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    This submits a creator application tied to your Kalovate account. We review
                    creator applications on a rolling basis and follow up by email.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Who We're Looking For */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-foreground mb-4">Who's a Good Fit?</h2>
            <p className="text-lg text-muted-foreground">
              We review every application individually. Here's who tends to do well on Kalovate.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6 space-y-2">
                <h4 className="font-semibold text-foreground">You have a real skill to teach</h4>
                <p className="text-sm text-muted-foreground">
                  Hands-on instructors, working professionals, and passionate hobbyists with
                  meaningful experience — not just enthusiasm.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-2">
                <h4 className="font-semibold text-foreground">You prefer small groups</h4>
                <p className="text-sm text-muted-foreground">
                  Kalovate isn't built for mass-enrollment video courses. It's built for intimate
                  workshops where students get real attention.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-2">
                <h4 className="font-semibold text-foreground">
                  You're local (or want to go online)
                </h4>
                <p className="text-sm text-muted-foreground">
                  We're rooted in the Hoosier Hills — South Central Indiana. If you're based here or
                  want to reach this market, you're in the right place. Online creators are also
                  welcome.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 space-y-2">
                <h4 className="font-semibold text-foreground">
                  You can commit to scheduled sessions
                </h4>
                <p className="text-sm text-muted-foreground">
                  Students book specific dates and times. We look for creators who can follow
                  through reliably and communicate when things change.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
